import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { tenantApi, LawmaConversionEvent } from '../lib/tenant-api';
import { format } from 'date-fns';
import { ActivityIcon } from 'lucide-react';
import { PageLoader, Pagination } from '../components/common';

export default function ConversionEvents() {
    const [page, setPage] = useState(0);
    const rowsPerPage = 50;

    const { data, isLoading, isError, error } = useQuery({
        queryKey: ['conversionEvents', page, rowsPerPage],
        queryFn: () => tenantApi.getConversionEvents(rowsPerPage, page * rowsPerPage),
    });

    const events = data?.events || [];
    const total = data?.total || 0;

    if (isLoading) {
        return <PageLoader />;
    }

    if (isError) {
        return (
            <div className="flex h-64 flex-col items-center justify-center text-center">
                <ActivityIcon className="mb-4 h-12 w-12 text-red-500" />
                <h3 className="text-lg font-medium text-gray-900">Error loading high intent events</h3>
                <p className="mt-2 text-sm text-gray-500">
                    {(error as Error)?.message || 'There was a problem loading the high intent events.'}
                </p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">High Intent Actions</h1>
                    <p className="mt-1 text-sm text-gray-500">
                        A comprehensive log of activities that could signify possible conversions (CTA clicks, form interactions, etc).
                    </p>
                </div>
            </div>

            <div className="overflow-x-auto bg-white shadow ring-1 ring-black ring-opacity-5">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                Timestamp
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                Event Type
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                Session ID
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                Event Details
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                Status
                            </th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white">
                        {events.length === 0 ? (
                            <tr>
                                <td colSpan={5} className="px-6 py-10 text-center text-sm text-gray-500">
                                    <ActivityIcon className="mx-auto mb-3 h-8 w-8 text-gray-400" />
                                    No conversion events found yet.
                                </td>
                            </tr>
                        ) : (
                            events.map((event: LawmaConversionEvent) => (
                                <tr key={event.id} className="hover:bg-gray-50">
                                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-900">
                                        {format(new Date(event.created_at), 'MMM d, yyyy HH:mm')}
                                    </td>
                                    <td className="whitespace-nowrap px-6 py-4">
                                        <span className="inline-flex rounded-full bg-indigo-100 px-2.5 py-0.5 text-xs font-medium text-indigo-800">
                                            {event.event_type}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 text-sm text-gray-500">
                                        <span className="font-mono text-xs truncate max-w-[150px] inline-block" title={event.session_id}>
                                            {event.session_id}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 text-sm text-gray-900">
                                        <div className="flex flex-col space-y-1">
                                            {event.event_data?.ctaName && (
                                                <div><span className="font-medium text-gray-500 text-xs">CTA:</span> {event.event_data.ctaName}</div>
                                            )}
                                            {event.event_data?.formKey && (
                                                <div><span className="font-medium text-gray-500 text-xs">Form:</span> {event.event_data.formKey}</div>
                                            )}
                                            {(!event.event_data?.ctaName && !event.event_data?.formKey) && (
                                                <span className="text-gray-400 italic">No additional details</span>
                                            )}
                                        </div>
                                    </td>
                                    <td className="whitespace-nowrap px-6 py-4 text-sm">
                                        {event.lead_id ? (
                                            <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">
                                                Upgraded to Lead
                                            </span>
                                        ) : (
                                            <span className="inline-flex rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-800">
                                                LCE
                                            </span>
                                        )}
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>

                {/* Pagination */}
                {total > rowsPerPage && (
                    <Pagination
                        className="border-t border-gray-200 bg-white px-4 py-3 sm:px-6"
                        page={page}
                        pageBase={0}
                        totalPages={Math.max(1, Math.ceil(total / rowsPerPage))}
                        onPageChange={setPage}
                        rangeStart={total === 0 ? 0 : page * rowsPerPage + 1}
                        rangeEnd={Math.min((page + 1) * rowsPerPage, total)}
                        totalItems={total}
                    />
                )}
            </div>
        </div>
    );
}
